import React, { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSocial } from "../../contexts/SocialContext";
import * as api from "../../services/api";
import { cn } from "../../lib/utils";
import { Group } from "../profile/ProfileParts";
import Avatar from "./Avatar";

// This week's board across the user and their mates: sessions + minutes since
// the week started (server counts from the ISO week boundary). Each row also
// carries the person's current streak, which used to be a second leaderboard
// card of its own. Server: GET /api/friends/leaderboard and /friends/streaks.
export default function WeeklyLeaderboard() {
  const { user } = useAuth();
  const { streaks } = useSocial();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getFriendsLeaderboard()
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!rows || rows.length < 2) return null; // needs at least one mate to be a board

  const streakFor = (entry, isSelf) => {
    if (isSelf) return streaks.self?.current_streak || 0;
    const match = streaks.friends.find((f) => Number(f.id) === Number(entry.id));
    return match?.current_streak || 0;
  };

  return (
    <Group
      title="This week"
      note="since Monday"
      footer="The flame is each person's current streak, in days."
    >
      {rows.map((entry, idx) => {
        const isSelf = Number(entry.id) === Number(user?.id);
        const streak = streakFor(entry, isSelf);
        const sessions = Number(entry.sessions_this_week) || 0;
        const minutes = Number(entry.minutes_this_week) || 0;
        return (
          <div
            key={entry.id}
            className={cn(
              "grid grid-cols-[1.125rem_1.625rem_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2.5",
              isSelf && "bg-primary/10",
            )}
          >
            <span className="text-center text-xs tabular-nums text-muted-foreground">{idx + 1}</span>
            <Avatar name={isSelf ? "You" : entry.name} photo={entry.photo_url} className="h-[26px] w-[26px]" />
            <span className="truncate text-sm font-medium">{isSelf ? "You" : entry.name}</span>
            <span className="flex items-center gap-2 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
              {sessions} session{sessions === 1 ? "" : "s"}
              {minutes > 0 ? ` · ${minutes} min` : ""}
              <span
                className={cn(
                  "inline-flex min-w-[2rem] items-center justify-end gap-0.5",
                  streak ? "font-semibold text-orange-400" : "text-muted-foreground/60",
                )}
                aria-label={`${streak}-day streak`}
              >
                <Flame className="h-3.5 w-3.5" />
                {streak}
              </span>
            </span>
          </div>
        );
      })}
    </Group>
  );
}
